// Dev / test mode flag (frontend).
//
// PM 2026-06-10 (rotation fix): the previous version of this file rotated
// puzzles every 6 minutes on the frontend ONLY, but the Worker still
// served today's UTC-midnight puzzle from D1. Bowz noticed the puzzles
// were not rotating in test mode - because the rotation never actually
// reached the Worker.
//
// Fix in this file:
//   - Detect `?test=1` at boot and persist it to localStorage.
//   - Re-export `getTestModeFetchInit()` so `lib/api.ts` can inject the
//     `X-Gives-Test-Mode: 1` header AND the `?test=1` query string on
//     every Worker call (belt-and-braces - middleware may strip one).
//   - The OLD `getCurrentPuzzleIndex()` helper is GONE: rotation is now
//     entirely server-side. The frontend no longer pretends to compute
//     a puzzle index locally.
//   - `mountTestModeBadge` is kept as a stub forwarding to
//     `mountTestModeBanner` (the new countdown chip), so existing callers
//     do not break.

import {
  TEST_MODE_BUCKET_MIN,
  nextBucketAt as nextBucketAtShared,
} from '@nostr-gives/shared';

const FLAG_KEY = 'gives.testMode';

/**
 * Read the URL `?test=` param and update the localStorage flag accordingly.
 * Call once at boot, BEFORE any Worker request goes out.
 */
export function syncTestModeFromUrl(): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  const raw = params.get('test');
  if (raw === null) return;

  try {
    if (raw === '1') localStorage.setItem(FLAG_KEY, '1');
    else if (raw === '0') localStorage.removeItem(FLAG_KEY);
  } catch {
    // ignore - storage may be unavailable in private mode
  }
}

/** Whether test mode is currently active. */
export function isTestMode(): boolean {
  try {
    return localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Headers to inject on every Worker request when test mode is active.
 * Returns an empty record otherwise so callers can spread unconditionally.
 */
export function getTestModeHeaders(): Record<string, string> {
  return isTestMode() ? { 'X-Gives-Test-Mode': '1' } : {};
}

/**
 * Append `test=1` to a URL path or full URL when test mode is active.
 * Belt-and-braces alongside `getTestModeHeaders()` - whichever survives
 * the CDN edge wins.
 */
export function appendTestModeQuery(pathOrUrl: string): string {
  if (!isTestMode()) return pathOrUrl;
  // Both `/path?x=1` and `https://host/path?x=1` work because we just
  // splice on the `?` separator.
  const sep = pathOrUrl.includes('?') ? '&' : '?';
  return `${pathOrUrl}${sep}test=1`;
}

/**
 * Epoch ms at which the next 10-minute bucket begins. Used by
 * `mountTestModeBanner` to drive its countdown.
 */
export function nextBucketAtMs(now: Date = new Date()): number {
  return nextBucketAtShared(now);
}

/** Re-export the canonical bucket-minutes constant. */
export const TEST_MODE_BUCKET_MINUTES = TEST_MODE_BUCKET_MIN;

// ---- Compatibility shims for existing call sites ------------------------
//
// PM 2026-06-10 (rotation fix): per-game `getCurrentPuzzleIndex()` and
// the standalone `mountTestModeBadge()` are obsolete. Rotation is now
// server-side via header + query. Games SHOULD drop these call sites in
// follow-up turns; for this commit we keep the names exported so the
// six game modules continue to compile while a parallel agent migrates
// them to send `getTestModeHeaders()` instead of `puzzleIndex`.

/**
 * @deprecated Use server-side rotation via `getTestModeHeaders()` /
 *             `appendTestModeQuery()` instead. Returning null here means
 *             games no longer attempt to pin a `puzzleIndex` on /start -
 *             the worker picks from the 10-minute bucket on its own.
 */
export function getCurrentPuzzleIndex(
  _gameId: string,
  _puzzleCount: number,
): number | null {
  return null;
}

/**
 * @deprecated Replaced by `mountTestModeBanner` (countdown chip). Kept as
 *             a no-op so old `main.ts` imports still resolve. The new
 *             banner is mounted on `document.body` from `main.ts` directly.
 */
export function mountTestModeBadge(_host: HTMLElement): { destroy: () => void } {
  return { destroy: (): void => {} };
}
